import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@ekhodealer/ekho-common/rev';

import type { UseMutationResult } from '@tanstack/react-query';

import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

interface RefundPreorderResponse {
	/** Total amount refunded to the customer */
	totalRefundedAmount: number;
	/** Total reverse transfer amount */
	totalReverseTransferAmount: number;
	/** Total processing fees refunded by Ekho */
	totalProcessingFeesEkhoRefunded: number;
}

export interface RefundPreorderParams {
	/** The preorder ID to refund */
	preorderId: string;
	/** The services provider ID */
	servicesProviderId: string;
}

/**
 * API function to cancel and refund a preorder
 */
const refundPreorder = async ({
	preorderId,
	servicesProviderId,
}: RefundPreorderParams): Promise<RefundPreorderResponse> => {
	const response = await apiClient.post(
		preordersEndpoints.refund(servicesProviderId, preorderId),
	);
	return response.data as RefundPreorderResponse;
};

/**
 * Hook for canceling and refunding a preorder
 * Invalidates preorder queries on success to refresh the table and details
 */
export const useRefundPreorder = (): UseMutationResult<RefundPreorderResponse, Error, RefundPreorderParams> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: refundPreorder,
		onSuccess: () => {
			toast({
				message: 'Preorder canceled and refunded successfully',
				variant: 'success',
			});

			// Invalidate preorder list and stats to refresh the table
			queryClient.invalidateQueries({
				queryKey: queries.preorders._def,
			});
		},
		onError: (error) => {
			toast({
				message: `Failed to refund preorder: ${(error as Error).message}`,
				variant: 'error',
			});
		},
	});
};
